import { Component, HostListener, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  finalize,
  first,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { Constants } from 'src/app/core/constants';
import { DetailedOrganizationFormModel } from 'src/app/core/models/organization/detailed-organization-form.model';
import { DetailedOrganization } from 'src/app/core/models/organization/detailed-organization.interface';
import { MinimalProfile } from 'src/app/core/models/user/profile';
import { EmployeeService } from 'src/app/core/services/employee.service';
import { OrganizationService } from 'src/app/core/services/organization.service';
import { FileService } from 'src/app/core/services/file.service';
import { ObservableUtil } from 'src/app/core/utils/observable-util';
import { EditableOrganization } from '../editable-organization';
import { PermissionService } from 'src/app/core/services/permission.service';
import { ResourceTypes } from 'src/app/core/enums/resource-types.enum';
import { UserCompanyPermissions } from 'src/app/core/enums/user-company-permissions.enum';
import { ThemeService } from 'src/app/core/services/theme.service';
import { ConfirmModalComponent } from 'src/app/core/components/confirm-modal/confirm-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { Util } from 'src/app/core/utils/util';
import { TabItem } from 'src/app/core/models/tabs/tab-item.interface';
import { Contact } from 'src/app/core/models/contact/contact.interface';
import { ContactService } from 'src/app/core/services/contact.service';
import { ProjectService } from 'src/app/core/services/project.service';

interface TabConfig {
  id: string;
  labelKey: string;
  icon?: TabItem['icon'];
  isVisible$: Observable<boolean>;
}

@Component({
  selector: 'app-organization-overview',
  templateUrl: './organization-overview.component.html',
  styleUrl: './organization-overview.component.scss',
  providers: [DetailedOrganizationFormModel],
  standalone: false,
})
export class OrganizationOverviewComponent extends EditableOrganization {
  organizationForm = inject(DetailedOrganizationFormModel);
  private readonly organizationService = inject(OrganizationService);
  private readonly employeeService = inject(EmployeeService);
  private readonly fileService = inject(FileService);
  private readonly contactService = inject(ContactService);
  private readonly permissionService = inject(PermissionService);
  private readonly projectService = inject(ProjectService);
  private readonly themeService = inject(ThemeService);
  private readonly translateService = inject(TranslateService);
  private readonly modalService = inject(NgbModal);
  private readonly router = inject(Router);

  public readonly notFound = Constants.NotFound;
  public readonly socialUrls = Constants.SocialUrls;

  public readonly canViewContacts$ =
    this.permissionService.userPermissions$.pipe(
      map((permissions) =>
        this.permissionService.hasPermission(
          permissions,
          ResourceTypes.Contact,
          UserCompanyPermissions.View,
        ),
      ),
      shareReplay(),
    );

  public readonly canEditOrganization$ =
    this.permissionService.userPermissions$.pipe(
      map((permissions) =>
        this.permissionService.hasPermission(
          permissions,
          ResourceTypes.Organization,
          UserCompanyPermissions.Edit,
        ),
      ),
    );

  public readonly canDeleteOrganization$ =
    this.permissionService.userPermissions$.pipe(
      map((permissions) =>
        this.permissionService.hasPermission(
          permissions,
          ResourceTypes.Organization,
          UserCompanyPermissions.Delete,
        ),
      ),
    );

  public isLoading = false;
  public isMobileViewport = false;
  public companyId = '';
  public organizationId = '';
  public organizationsBackUrl = '';
  public isCreateContactModalOpen = false;
  public isCreateProjectModalOpen = false;

  public organization$ = new BehaviorSubject<DetailedOrganization | null>(null);
  public readonly isCustomerOrganization$ = this.organization$.pipe(
    map((organization) => !!organization?.isCustomer),
    shareReplay(1),
  );

  public companyEmployees$: Observable<MinimalProfile[]>;
  public responsibleUser$: Observable<MinimalProfile | null>;

  public theme$ = this.themeService.themeChanges;

  private readonly companyId$ = new BehaviorSubject<string>('');
  private readonly organizationId$ = new BehaviorSubject<string>('');

  public readonly tabs: readonly TabConfig[] = [
    {
      id: 'contacts',
      labelKey: 'COMPONENTS.ORGANIZATION_OVERVIEW.TABS.CONTACTS',
      icon: 'users',
      isVisible$: this.canViewContacts$,
    },
    {
      id: 'projects',
      labelKey: 'COMPONENTS.ORGANIZATION_OVERVIEW.TABS.PROJECTS',
      icon: 'chart-bar',
      isVisible$: of(true),
    },
  ];

  private readonly activeTabSubject$ = new BehaviorSubject<string>('contacts');
  public readonly activeTab$ = this.activeTabSubject$.asObservable();

  public readonly visibleTabs$ = combineLatest(
    this.tabs.map((tab) => tab.isVisible$),
  ).pipe(
    map((visibilityMap) =>
      this.tabs.filter((_, index) => visibilityMap[index]),
    ),
    tap((visibleTabs) => {
      const activeTabId = this.activeTabSubject$.getValue();

      if (
        visibleTabs.length > 0 &&
        !visibleTabs.some((tab) => tab.id === activeTabId)
      ) {
        this.activeTabSubject$.next(visibleTabs[0].id);
      }
    }),
    shareReplay(1),
  );

  public readonly tabItems$ = this.visibleTabs$.pipe(
    map((tabs) =>
      tabs.map(
        (tab): TabItem => ({
          id: tab.id,
          labelKey: tab.labelKey,
          icon: tab.icon,
        }),
      ),
    ),
  );

  constructor() {
    const route = inject(ActivatedRoute);

    super();

    this.updateViewportState();

    const initUrls$ = route.params.pipe(
      first(),
      tap((params) => {
        this.organizationId = params['id'];
        this.companyId = route.parent?.parent?.snapshot.params['id'] ?? '';
        this.organizationsBackUrl = `/companies/${this.companyId}/crm/organizations`;

        this.organizationId$.next(this.organizationId);
        this.companyId$.next(this.companyId);

        this.organizationService.setOrganizationsUrl(
          this.companyId,
        );
        this.employeeService.setEmployeesUrl(this.companyId);
        this.contactService.setContactsUrl(this.companyId);
        this.projectService.setProjectsUrl(this.companyId);
        this.projectService.setCompanyId(this.companyId);
      }),
    );

    initUrls$.subscribe();

    combineLatest([initUrls$, this.organizationId$])
      .pipe(
        switchMap(([, organizationId]) =>
          this.organizationService.getOrganization(organizationId),
        ),
        tap((organization) => {
          this.organization$.next(organization);

          this.loadSocialSegments(organization);

          const formValues = { ...organization };
          delete formValues.socialUrls;

          this.organizationForm.form.patchValue(formValues);
        }),
      )
      .subscribe();

    this.companyEmployees$ = initUrls$.pipe(
      switchMap(() => this.employeeService.getCompanyEmployeesLookup()),
      shareReplay(),
    );

    this.responsibleUser$ = combineLatest([
      this.companyEmployees$,
      this.organization$,
    ]).pipe(
      map(
        ([employees, organization]) =>
          employees?.find((e) => e.id === organization?.responsibleUserId) ||
          null,
      ),
      filter((x) => !!x),
      switchMap((profile) =>
        ObservableUtil.getProfileWithImage$(profile, this.fileService),
      ),
    );
  }

  public onUpdateInput(fieldName: string): void {
    this.isLoading = true;

    const newValue = this.organizationForm.form.get(fieldName)?.value;

    this.organizationId$
      .pipe(
        first(),
        switchMap((organizationId) =>
          this.organizationService
            .patchOrganization(organizationId, fieldName, newValue)
            .pipe(
              first(),
              tap(() => {
                const currentOrganization = this.organization$.getValue()!;

                const updatedOrganization = {
                  ...currentOrganization,
                  [fieldName]: newValue,
                };

                this.organization$.next(updatedOrganization);
              }),
              finalize(() => (this.isLoading = false)),
            ),
        ),
      )
      .subscribe();
  }

  public onUpdateSocialInput(fieldName: string): void {
    this.isLoading = true;

    const instagramUrl = `${this.socialUrls.instagram}/${this.organizationForm.form.controls.social.controls.instagramSegment.value || ''}`;
    const facebookUrl = `${this.socialUrls.facebook}/${this.organizationForm.form.controls.social.controls.facebookSegment.value || ''}`;
    const linkedInUrl = `${this.socialUrls.linkedIn}/${this.organizationForm.form.controls.social.controls.linkedInSegment.value || ''}`;

    this.organizationId$
      .pipe(
        first(),
        switchMap((organizationId) =>
          this.organizationService
            .patchOrganization(
              organizationId,
              'socialUrls',
              JSON.stringify([instagramUrl, facebookUrl, linkedInUrl]),
            )
            .pipe(
              first(),
              tap(() => {
                const currentOrganization = this.organization$.getValue()!;

                const updatedOrganization = {
                  ...currentOrganization,
                  social: {
                    ...currentOrganization.social,
                    [fieldName]:
                      this.organizationForm.form.controls.social.get(fieldName)
                        ?.value,
                  },
                };

                this.organization$.next(updatedOrganization);
              }),
              finalize(() => (this.isLoading = false)),
            ),
        ),
      )
      .subscribe();
  }

  public updateResponsibleUser(fieldName: string, popover: NgbPopover): void {
    this.onUpdateInput(fieldName);
    popover.close();
  }

  public deleteOrganization(): void {
    const modalRef = this.modalService.open(ConfirmModalComponent, {
      size: 'sm',
    });

    modalRef.componentInstance.message = Util.translateWithParam(
      this.translateService,
      `WARNINGS.DELETE`,
      {
        uk: 'організацію',
        en: 'organization',
      },
    );

    modalRef.closed
      .pipe(
        first(),
        filter(Boolean),
        withLatestFrom(this.organizationId$),
        switchMap(([, organizationId]) =>
          this.organizationService.deleteOrganization(organizationId),
        ),
        tap(() => {
          this.router.navigateByUrl(
            `companies/${this.companyId$.getValue()}/crm/organizations`,
          );
        }),
      )
      .subscribe();
  }

  public selectTab(tabId: string): void {
    this.activeTabSubject$.next(tabId);
  }

  public openCreateContactModal(): void {
    this.isCreateContactModalOpen = true;
  }

  public closeCreateContactModal(): void {
    this.isCreateContactModalOpen = false;
  }

  public onContactCreated(contact: Contact): void {
    this.closeCreateContactModal();
    this.isLoading = true;

    this.contactService
      .patchContact(contact.id, 'organizationId', this.organizationId)
      .pipe(
        first(),
        tap(() => {
          this.contactService.addToOrganization(this.companyId, this.organizationId, contact);
        }),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe();
  }

  public openCreateProjectModal(): void {
    this.isCreateProjectModalOpen = true;
  }

  public closeCreateProjectModal(): void {
    this.isCreateProjectModalOpen = false;
  }

  public onCreateProjectSuccess(): void {
    this.closeCreateProjectModal();
    this.projectService.invalidateOrganizationProjects(this.companyId, this.organizationId);
    this.projectService.invalidateProjectsForSearch(this.companyId);
    this.projectService.loadOrganizationProjects(this.companyId, this.organizationId).subscribe();
    this.projectService.loadProjectsForSearch(this.companyId, this.organizationId).subscribe();
  }

  @HostListener('window:resize')
  public onWindowResize(): void {
    this.updateViewportState();
  }

  private loadSocialSegments(organization: DetailedOrganization) {
    organization.social = {};

    if (!organization.socialUrls) {
      return;
    }

    try {
      const urls: string[] = JSON.parse(organization.socialUrls);

      for (const url of urls) {
        const { hostname, pathname } = new URL(url);
        const parts = pathname.split('/').filter(Boolean);
        const segment = parts.length > 0 ? parts[parts.length - 1] : '';

        if (hostname.includes('instagram')) {
          organization.social.instagramSegment = segment;
        } else if (hostname.includes('facebook')) {
          organization.social.facebookSegment = segment;
        } else if (hostname.includes('linkedin') && parts[0] === 'in') {
          organization.social.linkedInSegment = parts[1];
        }
      }
    } catch (err) {
      console.error('Invalid social URLs JSON', err);
    }
  }

  private updateViewportState(): void {
    this.isMobileViewport = typeof window !== 'undefined' && window.innerWidth < 768;
  }
}
